import React, {
  memo, useMemo, useCallback, useState,
} from 'react'
import { format } from 'date-fns'
import Link from 'next/link'
import Image from 'next/legacy/image'
import clsx from 'clsx'
import { FormattedMessage } from 'react-intl'
import Paper from '@mui/material/Paper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper'
import Typography from '@mui/material/Typography'
import NumberFormat from 'react-number-format'
import Grid from '@mui/material/Grid2'
import {
  getVehicleTitle,
  formatLargeTransmission,
} from 'utils/vehicles'
import UpHandIcon from 'components/icons/UpHandIcon.svg'
import DownHandIcon from 'components/icons/DownHandIcon.svg'
import Box from '@mui/material/Box'
import usePageProps from 'hooks/usePageProps'
import NoImage from 'public/img/No_foto_AutoSRP.webp'
import LocationIcon from 'components/icons/location.svg'
import { titleCaps } from 'utils/universal'
import AuctionCountdownTimer from 'components/Auction/AuctionCountdownTimer'
import BranchIcon from 'components/icons/branchIcon.svg'
import { useSelector } from 'react-redux'
import useNonInitialEffect from 'hooks/useNonInitialEffect'
import sx from './styles'

const VehicleAuctionCard = (props) => {
  const {
    vehicle,
    className: classNameProp = '',
    direction = 'column',
    swiper = false,
    href,
    sx: sxProp = {},
    priority = false,
    swiperProps = {},
  } = props

  const { temporaryImageDomain, quality = 60 } = usePageProps()
  const className = useMemo(() => clsx('VehicleCard-root', classNameProp, direction), [classNameProp, direction])
  const titleCar = useMemo(() => getVehicleTitle(vehicle), [vehicle])
  const { auctions } = useSelector((s) => s.auction)
  const actionEndsAt = new Date(vehicle?.auction?.auctionEvent?.status === undefined ? '2023-07-31T13:30:31.000-05:00' : vehicle?.auction?.auctionEvent?.ends_at)
  const auctionStartAt = new Date(vehicle?.auction?.auctionEvent?.status === undefined ? '2023-07-31T13:30:31.000-05:00' : vehicle?.auction?.auctionEvent?.starts_at)
  const auctionStatus = (vehicle?.auction?.auctionEvent?.status === undefined ? 'scheduled' : vehicle?.auction?.auctionEvent?.status)
  const [thisAuctionHasBids, setThisAuctionHasBids] = useState(Boolean(vehicle?.auction?.topBid))
  const [iAlreadyOffered, setIAlreadyOffered] = useState(Boolean(vehicle?.auction?.lastUserBid))
  const [topBid, setTopBid] = useState(vehicle?.auction?.topBid || vehicle?.auction?.minBid)
  const isAuctionFinished = vehicle?.auction?.auctionEvent?.status === 'finished'
  const [myOfferIsWinner, setMyOfferIsWinner] = useState(
    vehicle?.auction?.topBid === vehicle?.auction?.lastUserBid?.amount,
  )

  let vehicleIVA = null
  if ((vehicle?.vehicleLegal?.iva_type !== null) && (vehicle?.vehicleLegal?.iva_type !== 'SIN INFORMACIÓN')) {
    vehicleIVA = vehicle?.vehicleLegal?.iva_type
  }

  const [priceOfferted, setPriceOfferted] = useState(
    isAuctionFinished
      ? vehicle?.auction?.topBid
      : vehicle?.auction?.lastUserBid
        ? vehicle?.auction?.lastUserBid?.amount
        : vehicle?.auction?.topBid
          ? vehicle?.auction?.topBid
          : vehicle?.auction?.minBid
            ? vehicle?.auction?.minBid
            : 0,
  )

  const mainText = useMemo(
    () => (vehicle?.auction?.auctionEvent?.status === 'finished'
      ? 'AUCTION_PURCHASE_PRICE'
      : thisAuctionHasBids
        ? iAlreadyOffered
          ? 'AUCTIONS_YOUR_LAST_OFFERT'
          : 'AUCTIONS_CURRENT_OFFER'
        : 'AUCTIONS_INITIAL_PRICE'),
    [vehicle?.auction?.auctionEvent?.status, thisAuctionHasBids, iAlreadyOffered],
  )

  useNonInitialEffect(() => {
    // valid if topBid and lastOffer is equal
    // eslint-disable-next-line no-console
    console.log('useNonInitialEffect')
    const auctionSelected = auctions?.find((i) => i.auctionId === vehicle?.auction?.id)
    // eslint-disable-next-line no-console
    console.log('auctionSelected', auctionSelected)

    if (auctionSelected) {
      if (auctionSelected?.lastOffer === auctionSelected?.topBid) {
        setMyOfferIsWinner(true)
        setPriceOfferted(auctionSelected?.lastOffer)
      } else {
        setMyOfferIsWinner(false)
        setTopBid(auctionSelected?.topBid)
      }
    }

    // if iAlreadyOffered is false and auctionSelected?.lastOffer is true
    // then change the iAlreadyOffered to true
    if (!iAlreadyOffered && auctionSelected?.lastOffer) {
      setIAlreadyOffered(true)
    }

    if (!thisAuctionHasBids && auctionSelected?.topBid) {
      setThisAuctionHasBids(true)
    }
  }, [auctions])

  const renderImage = useCallback((image) => {
    return (
      <>
        {image ? (
          <Image
            src={image?.origin_image_url
              ? `${image?.origin_image_url}/-/format/webp/-/resize/300x200/-/quality/smart/`
              : `${temporaryImageDomain}/300x200/filters:format(webp)/filters:quality(${quality})/${image.filename}`}
            style={{ objectFit: 'cover' }}
            blurDataURL={image?.origin_image_url
              ? `${image?.origin_image_url}/-/blur/100/-/format/webp/-/resize/10x6/-/quality/smart/`
              : `${temporaryImageDomain}/10x6/filters:blur(90)/filters:format(webp)/filters:quality(${quality})/${image.filename}`}
            placeholder="blur"
            layout="fill"
            priority={priority}
            title={`Autos seminuevos y autos usados ${titleCar} al mejor precio en ClikAuto`}
            alt={`Autos seminuevos, ${titleCar}`}
            unoptimized
          />
        ) : (
          <Image
            src={NoImage}
            style={{ objectFit: 'contain' }}
            layout="fill"
            alt="Default photo"
            title={`Autos seminuevos, ${titleCar}`}
            sx={{
              marginTop: '20px',
            }}
          />
        )}

      </>
    )
  }, [
    titleCar,
    vehicle,
  ])

  const profilePhoto = useMemo(() => (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        height: 130,
        marginTop: '20px',
      }}
    >
      {renderImage(vehicle?.images?.[0])}
    </Box>
  ), [vehicle])

  const getFirstNPhotos = useCallback((n) => ([
    vehicle?.images[0],
    ...vehicle?.images
      .filter((_, i) => i > 0)
      .sort(() => Math.random() - 0.5)
      .filter((_, i) => i < n - 1),
  ]), [vehicle])

  const imageSwiper = useMemo(() => (
    <Box
      sx={sx.imageSwiper}
      className={clsx({
        finished: isAuctionFinished,
      })}
    >
      <Swiper
        navigation
        modules={[Navigation]}
        slidesPerView={2}
        spaceBetween={4}
        preventClicksPropagation
        {...swiperProps}
      >
        {vehicle?.images?.length ? (
          getFirstNPhotos(4).map((image) => {
            return (
              <SwiperSlide key={image.filename}>
                {renderImage(image, false)}
              </SwiperSlide>
            )
          })
        ) : (
          <>
            <SwiperSlide>{renderImage()}</SwiperSlide>
            <SwiperSlide>{renderImage()}</SwiperSlide>
          </>
        )}
      </Swiper>
    </Box>
  ), [vehicle, isAuctionFinished])

  const title = (
    <Typography
      variant="h4"
      sx={sx.titleCar}
    >
      {titleCar}
    </Typography>
  )

  const subtitle = (
    <Typography
      sx={sx.breakSpace}
      variant="body1"
      noWrap
    >
      {vehicle.trim && (
        <>
          <span>{titleCaps(vehicle.trim)}</span>
          &bull;
        </>
      )}
      <span>
        <NumberFormat
          value={vehicle.mileage}
          suffix=" kms"
          thousandSeparator
          displayType="text"
        />
        &bull;
      </span>
      {vehicle?.cesviTransmission && (
        <>
          <span>
            {formatLargeTransmission(vehicle?.cesviTransmission)}
          </span>
        </>
      )}

    </Typography>
  )

  const priceContent = (
    <Box
      sx={sx.priceContainer}
      className={clsx({
        iAlreadyOffered,
      })}
    >
      <Typography
        variant="strong"
        component="p"
      >
        <FormattedMessage id={mainText} />
        <Typography
          variant="subtitle1"
          component="span"
          ml={1}
          sx={sx.price}
          className={clsx({
            blue: !thisAuctionHasBids,
          })}
        >
          <NumberFormat
            value={iAlreadyOffered
              ? priceOfferted
              : topBid}
            displayType="text"
            prefix="$"
            thousandSeparator
            decimalScale={0}
          />
        </Typography>
      </Typography>

      {isAuctionFinished ? (
        <Typography
          variant="caption"
          component="p"
        >
          <FormattedMessage
            id="AUCTION_DATE_OF_PURCHASE"
            values={{
              date: format(new Date(vehicle?.auction?.lastUserBid?.created_at), 'dd/MM/yyyy'),
            }}
          />
        </Typography>
      ) : (
        (thisAuctionHasBids && iAlreadyOffered) && (
          <Typography
            variant="caption"
            component="p"
          >
            <Typography
              component="span"
              variant="caption"
            >
              {myOfferIsWinner ? <UpHandIcon /> : <DownHandIcon />}
            </Typography>
            <FormattedMessage id={myOfferIsWinner ? 'AUCTION_YOUR_OFFER_IS_WINNER' : 'AUCTION_YOUR_OFFER_IS_LOSER'} />
          </Typography>
        )
      )}
    </Box>
  )

  const auctionStatusTag = (
    <Box
      sx={sx.auctionStatusTag}
      className={clsx({
        open: auctionStatus === 'open',
        finished: auctionStatus === 'finished',
        scheduled: auctionStatus === 'scheduled',
      })}
    >
      <AuctionCountdownTimer
        auctionEnds={actionEndsAt}
        auctionStartAt={auctionStartAt}
        text={auctionStatus === 'open' ? 'AUCTIONS_ENDS_TIME_TITLE' : 'AUCTIONS_STARTS_TIME_TITLE'}
        status={auctionStatus}
        variant="srpVariant"
      />
    </Box>
  )

  const location = (
    <Grid
      container
      sx={sx.sellerContainer}
    >
      <Grid
        size="auto"
        pr={1}
      >
        <Typography
          variant="caption"
          sx={sx.sellerType}
        >
          <BranchIcon />
          ClikAuto Lomas Verde
        </Typography>
      </Grid>
      <Grid
        size="auto"
        pr={1}
      >
        <Typography
          variant="caption"
          sx={sx.locationText}
        >
          <LocationIcon />
          EDO. MEX.
        </Typography>
      </Grid>

    </Grid>
  )

  return (
    <Link
      href={href}
      passHref
    >
      <Paper
        sx={{ ...sx.root, ...sxProp }}
        className={clsx(className)}
        elevation={0}
        component="a"
        title={`Autos seminuevos, ${titleCar}`}
      >
        {auctionStatusTag}
        <Grid
          container
          spacing={1}
        >
          <Grid
            size="grow"
            xs={direction === 'row' ? 6 : 12}
          >
            {swiper && vehicle?.images.length > 0 ? imageSwiper : profilePhoto}
          </Grid>

          <Grid
            size={direction === 'row' ? 6 : 12}
            sx={sx.vehicleInfoContainer}
          >

            {title}

            {subtitle}

            {priceContent}

            {
              (vehicleIVA) && (
                <Typography
                  variant="caption"
                  component="p"
                >
                  <FormattedMessage
                    id="AUCTIONS_IVA"
                    values={{
                      IVA: `${vehicleIVA}`,
                    }}
                  />
                </Typography>
              )
            }

          </Grid>

          {location}

        </Grid>
      </Paper>
    </Link>
  )
}

export default memo(VehicleAuctionCard)
